import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowUpRight, Hammer, Trash2 } from "lucide-react";
import { api, dateTR, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const empty = { name: "", description: "", location: "", status: "aktif" };

export default function YapSat() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/yapsat").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Proje adı zorunlu");
    try {
      await api.post("/yapsat", form);
      toast.success("Eklendi"); setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => { if (!window.confirm("Proje ve tüm girişleri silinsin mi?")) return; await api.delete(`/yapsat/${id}`); load(); };

  return (
    <div data-testid="yapsat-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">Yap-Sat</p>
          <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl md:text-5xl">Kendi Projelerimiz</h1>
          <p className="text-white/55 mt-2 text-sm max-w-xl">Arsa, ruhsat, beton, satış geliri gibi kalemleri elle yöneterek her projenin kar/zarar durumunu net görün.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="yapsat-add" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#121212] hover:bg-white px-5 py-3 font-display uppercase tracking-[0.18em] text-xs transition">
              <Plus className="h-4 w-4" /> Yeni Proje
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#d4af37]/20 text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">Yap-Sat Projesi</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Proje Adı *"><input data-testid="yapsat-input-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="kc-input" required /></Field>
              <Field label="Konum"><input value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} className="kc-input" /></Field>
              <Field label="Açıklama"><textarea rows={2} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="kc-input resize-none" /></Field>
              <Field label="Durum">
                <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} className="kc-input">
                  <option value="aktif" className="bg-[#0a0a0a]">Aktif</option>
                  <option value="tamamlandi" className="bg-[#0a0a0a]">Tamamlandı</option>
                  <option value="planlama" className="bg-[#0a0a0a]">Planlama</option>
                </select>
              </Field>
              <button data-testid="yapsat-save" className="w-full bg-[#d4af37] text-[#121212] hover:bg-white py-3 font-display uppercase tracking-[0.2em] text-xs transition">Kaydet</button>
            </form>
            <KCStyle />
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="md:col-span-2 xl:col-span-3 border border-dashed border-white/10 p-12 text-center text-white/45 font-mono text-xs uppercase tracking-[0.3em]">Henüz yap-sat projesi eklenmedi</div>
        ) : items.map((y) => (
          <article key={y.id} className="border border-white/8 bg-[#121212] hover:border-[#d4af37]/40 transition" data-testid={`yapsat-row-${y.id}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <Hammer className="h-7 w-7 text-[#d4af37]" />
                <span className="text-[10px] font-mono uppercase tracking-[0.3em] bg-[#d4af37]/15 border border-[#d4af37]/30 text-[#d4af37] px-2 py-1">{y.status}</span>
              </div>
              <div className="font-display text-2xl tracking-tight">{y.name}</div>
              {y.location && <div className="text-white/55 text-sm mt-1">{y.location}</div>}
              {y.description && <p className="mt-2 text-white/60 text-sm line-clamp-2">{y.description}</p>}
              <div className="mt-5 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{dateTR(y.created_at)}</span>
                <div className="flex items-center gap-2">
                  <button onClick={()=>remove(y.id)} className="p-2 text-white/40 hover:text-[#d4af37]"><Trash2 className="h-4 w-4" /></button>
                  <Link to={`/admin/yapsat/${y.id}`} data-testid={`yapsat-open-${y.id}`} className="inline-flex items-center gap-2 text-[#d4af37] font-display text-xs uppercase tracking-[0.25em] hover:text-white transition">Aç <ArrowUpRight className="h-3.5 w-3.5" /></Link>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">{label}</div>{children}</label>
);
const KCStyle = () => <style>{`.kc-input{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.12);padding:10px 12px;color:white;outline:none}.kc-input:focus{border-color:#d4af37}`}</style>;
